// ABOUTME: Selects a destination and synchronizes it with a Formik field.
// ABOUTME: Supports debounced destination search and cursor-based pagination.
import { useContext, useEffect, useState } from "react";
import {
  GetDestinationsQuery,
  GetDestinationsQueryVariables,
  useGetDestinationsQuery,
} from "../../../../../generated/graphql";
import { AsyncSelect } from "../../async-select/AsyncSelect";
import { AsyncSelectData } from "../../async-select/AsyncSelectData";
import { useTranslation } from "react-i18next";
import AuthContext from "src/app/contexts/auth-context";

interface FormikFormSelectProps {
  fieldName: string;
  labelName?: string;
  intialId?: number;
  formik;
  showLabel?: boolean;
}

type DestinationNode = NonNullable<
  NonNullable<GetDestinationsQuery["destinations"]>["nodes"]
>[number];

const useDebouncedSearch = (searchInput: string) => {
  const [debouncedSearchInput, setDebouncedSearchInput] = useState("");

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedSearchInput(searchInput.trim());
    }, 300);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [searchInput]);

  return debouncedSearchInput;
};

const useDestinationOptions = (
  companyId: number,
  debouncedSearchInput: string
) => {
  const [allDestinations, setAllDestinations] = useState<DestinationNode[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [cursor, setCursor] = useState<string | null>(null);
  const [loadingMoreDestinations, setLoadingMoreDestinations] = useState(false);

  const destinationQueryVariables: GetDestinationsQueryVariables = {
    companyId,
    take: 15,
    cursor: undefined,
    search: debouncedSearchInput,
  };

  const {
    data: destinations,
    loading: loadingDestinations,
    fetchMore,
  } = useGetDestinationsQuery({
      variables: destinationQueryVariables,
      skip: !Number.isFinite(companyId) || companyId <= 0,
    });

  useEffect(() => {
    if (destinations?.destinations) {
      setAllDestinations(destinations.destinations.nodes || []);
      setHasMore(destinations.destinations.pageInfo?.hasNextPage || false);
      setCursor(destinations.destinations.pageInfo?.endCursor || null);
    }
  }, [destinations]);

  const handleLoadMore = async () => {
    if (
      !hasMore ||
      !cursor ||
      loadingDestinations ||
      loadingMoreDestinations
    ) {
      return;
    }

    const loadMoreVariables: GetDestinationsQueryVariables = {
      companyId,
      take: 15,
      cursor,
      search: debouncedSearchInput,
    };

    try {
      setLoadingMoreDestinations(true);
      await fetchMore({
        variables: loadMoreVariables,
      });
    } finally {
      setLoadingMoreDestinations(false);
    }
  };

  return {
    allDestinations,
    handleLoadMore,
    loadingDestinations,
    loadingMoreDestinations,
  };
};

const useInitialDestination = (
  allDestinations: DestinationNode[],
  initialId: number | undefined,
  fieldName: string,
  setFieldValue
) => {
  const [initialDestination, setInitialDestination] = useState<AsyncSelectData>();

  useEffect(() => {
    if (allDestinations.length === 0) {
      return;
    }

    const destination = allDestinations.find((item) => item.id === initialId);
    if (!destination) {
      setInitialDestination(undefined);
      setFieldValue(fieldName, 0);
      return;
    }

    setInitialDestination(
      new AsyncSelectData({
        title: destination.name ?? "",
        value: destination.id,
      })
    );
    window.setTimeout(() => {
      setFieldValue(fieldName, destination.id ?? 0);
    }, 200);
  }, [allDestinations, fieldName, initialId, setFieldValue]);

  return initialDestination;
};

const FormikDestinationFormSelect = (props: FormikFormSelectProps) => {
  const userService = useContext(AuthContext);
  const { t } = useTranslation();
  const [searchInput, setSearchInput] = useState("");
  const debouncedSearchInput = useDebouncedSearch(searchInput);
  const companyId = Number(userService.authContextData?.currentUser?.companyId);
  const {
    allDestinations,
    handleLoadMore,
    loadingDestinations,
    loadingMoreDestinations,
  } = useDestinationOptions(companyId, debouncedSearchInput);
  const formikSetFieldValue = props.formik.setFieldValue;
  const initialDestination = useInitialDestination(
    allDestinations,
    props.intialId,
    props.fieldName,
    formikSetFieldValue
  );

  const selectedDestinationChanged = (data: AsyncSelectData) => {
    formikSetFieldValue(props.fieldName, data.value);
  };

  return (
    <div className="mt-3">
      <AsyncSelect
        label={
          props.showLabel
            ? props.labelName || t("upsert.company.labels.destination")
            : ""
        }
        onItemSelected={selectedDestinationChanged}
        inputValueChanged={(_event: any, value: string) => {
          setSearchInput(value);
        }}
        onLoadMore={handleLoadMore}
        loadingMoreData={loadingMoreDestinations}
        defaultSelectOption={initialDestination}
        data={allDestinations.map(
          (destination) =>
            new AsyncSelectData({
              title: destination.name ?? "",
              value: destination.id,
            })
        )}
        loadingData={loadingDestinations}
        hasError={Boolean(
          props.formik.errors[props.fieldName] &&
            props.formik.touched[props.fieldName]
        )}
        className="w-full"
      />
    </div>
  );
};

export { FormikDestinationFormSelect };
